import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Search, Moon, Sun, X } from "lucide-react";
import { supabase } from "../../Utils/supabaseClient";
import { dashboardAPI } from "../../services/api";

function useDebounced(value, delay = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

export default function Header() {
  const navigate = useNavigate();
  
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debouncedQ = useDebounced(query);
  const searchRef = useRef();

  const [dark, setDark] = useState(() => {
    try {
      const s = localStorage.getItem("beetroot_dark");
      return s !== null ? s === "true" : false;
    } catch { return false; }
  });

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("beetroot_dark", String(dark));
  }, [dark]);

  // Search backend
  useEffect(() => {
    let cancelled = false;
    async function runSearch() {
      const q = debouncedQ.trim();
      if (!q || q.length < 2) { setResults([]); return; }
      
      setSearching(true);
      try {
        const out = [];

        // Page shortcuts
        [
          { text: "Overview",          route: "/dashboard" },
          { text: "User Analytics",    route: "/dashboard/user-analytics" },
          { text: "Content Analytics", route: "/dashboard/content-analytics" },
          { text: "AI Advisory",       route: "/dashboard/ai-advisory" },
          { text: "Admin Settings",    route: "/dashboard/settings" },
        ]
          .filter(p => p.text.toLowerCase().includes(q.toLowerCase()))
          .forEach(p => out.push({ type: "Page", id: p.route, text: p.text, meta: "Navigate", route: p.route }));

        // Backend search
        const [contentRes, usersRes] = await Promise.allSettled([
          dashboardAPI.getContentAnalytics("30d"),
          dashboardAPI.getUserAnalytics("30d"),
        ]);

        if (contentRes.status === "fulfilled") {
          (contentRes.value?.data?.topPosts || [])
            .filter(p => p.title?.toLowerCase().includes(q.toLowerCase()))
            .slice(0, 3)
            .forEach(p => out.push({ type: "Post", id: p.id, text: p.title, meta: `${p.totalEngagement || 0} engagement`, route: "/dashboard/content-analytics" }));
        }

        if (usersRes.status === "fulfilled") {
          (usersRes.value?.data?.topContributors || [])
            .filter(u => u.authorId?.toLowerCase().includes(q.toLowerCase()))
            .slice(0, 3)
            .forEach(u => out.push({ type: "User", id: u.authorId, text: `Author ${u.authorId?.slice(0, 16)}`, meta: `${u.postCount || 0} posts`, route: "/dashboard/user-analytics" }));
        }

        if (!cancelled) setResults(out.slice(0, 8));
      } catch { if (!cancelled) setResults([]); }
      finally { if (!cancelled) setSearching(false); }
    }
    runSearch();
    return () => { cancelled = true; };
  }, [debouncedQ]);

  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowResults(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleResultClick = useCallback((r) => {
    setShowResults(false);
    setQuery("");
    navigate(r.route);
  }, [navigate]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    sessionStorage.clear();
    navigate("/admin/login");
  };

  return (
    <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 md:px-6 py-3 flex-shrink-0">
      <div className="flex items-center justify-between gap-3">

        {/* Fixed title - never changes */}
        <div className="flex-shrink-0">
          <h1 className="text-lg font-bold text-gray-800 dark:text-gray-100">
            Beetroot Analytics
          </h1>
          <p className="text-xs text-gray-400 hidden sm:block">
            Real-time insights dashboard
          </p>
        </div>

        <div className="flex items-center gap-2">

          {/* Search */}
          <div className="relative" ref={searchRef}>
            <div className="relative">
              {searching ? (
                <span className="absolute left-3 top-2.5 w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              ) : (
                <Search size={14} className="absolute left-3 top-2.5 text-gray-400" />
              )}
              <input
                value={query}
                onChange={e => { setQuery(e.target.value); setShowResults(true); }}
                onFocus={() => query.length >= 2 && setShowResults(true)}
                placeholder="Search..."
                className="pl-9 pr-7 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 dark:text-gray-100 text-sm w-44 md:w-56 focus:outline-none focus:ring-2 focus:ring-red-400 transition"
              />
              {query && (
                <button onClick={() => { setQuery(""); setResults([]); setShowResults(false); }}
                  className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-700">
                  <X size={13} />
                </button>
              )}
            </div>

            {showResults && query.length >= 2 && results.length > 0 && (
              <div className="absolute right-0 mt-2 w-72 bg-white dark:bg-gray-800 border rounded-xl shadow-xl z-50">
                {results.map((r, i) => (
                  <div key={`${r.type}-${r.id || i}`}
                    onClick={() => handleResultClick(r)}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{r.text}</p>
                      <p className="text-xs text-gray-400">{r.type} · {r.meta}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dark mode */}
          <button onClick={() => setDark(p => !p)}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition">
            {dark ? <Sun size={18} className="text-yellow-400" /> : <Moon size={18} className="text-gray-600" />}
          </button>

          {/* Logout */}
          <button onClick={handleLogout}
            className="flex items-center gap-1.5 px-2 md:px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-red-600 rounded-lg transition">
            <LogOut size={16} />
            <span className="hidden md:inline text-xs">Logout</span>
          </button>
        </div>
      </div>
    </header>
  );
}